import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of, shareReplay } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { TabOrganizationService } from './tab-organization.service';
import { TokenService } from './token.service';

/** Must match UserSecurity.RefreshRequestHeader on the backend. */
const REFRESH_REQUEST_HEADER = 'X-TL-Refresh';

/** Must match UserSecurity.RequestedOrganizationHeader on the backend. */
const ORGANIZATION_HEADER = 'X-TL-Organization';

/** Must match UserSecurity.RefreshCookiePath - the browser matches a cookie's path literally. */
const REFRESH_ENDPOINT = 'Authenticate/refresh';

interface RefreshResult {
  body: string;
  expiryInUtc: string;
}

/**
 * Trades the HttpOnly refresh cookie for a new access token.
 *
 * `withCredentials` is the whole point of not putting this on RestClient: every other call in the
 * app is a bearer request and deliberately sends no cookie, and a shared helper that sent one would
 * be copied to endpoints that must not have it. This is the only request in the SPA that carries a
 * credential the browser attached rather than one we attached ourselves.
 */
@Injectable({
  providedIn: 'root',
})
export class TokenRefreshService {
  /**
   * The one refresh in flight, if any. Bootstrap, a 401 retry and a second tab's worth of parallel
   * requests can all ask at once; without this they would each spend the cookie separately and the
   * last answer would win. Cleared in `finalize` so the *next* refresh is a fresh request rather
   * than a replay of this one's result.
   */
  private inFlight: Observable<boolean> | null = null;

  constructor(
    private httpClient: HttpClient,
    private tokenService: TokenService,
    private tabOrganization: TabOrganizationService,
    @Inject('DS_API') private rootUrl: string,
  ) {}

  public refresh(): Observable<boolean> {
    if (this.inFlight) return this.inFlight;

    this.inFlight = this.httpClient
      .post<RefreshResult>(
        `${this.rootUrl}/${REFRESH_ENDPOINT}`,
        {},
        {
          withCredentials: true,
          // Presence is what matters, not the value: a cross-site form, image or navigation - the
          // shapes that carry a cookie without script - cannot set a header at all.
          headers: this.refreshHeaders(),
        },
      )
      .pipe(
        map((result) => {
          if (!result?.body) return false;
          this.tokenService.setToken(result.body, result.expiryInUtc);
          return true;
        }),
        catchError(() => {
          // Every failure is reported the same way - false, never a thrown error - and the caller
          // decides what it means. A 401 is the honest "the cookie is gone, expired, or past its
          // absolute cap"; a 5xx or a dropped connection is a fault. Neither leaves a usable token
          // behind, so both clear it, and neither is allowed to become an unhandled rejection in an
          // APP_INITIALIZER, where it would stop the app booting at all.
          this.tokenService.clearToken();
          return of(false);
        }),
        finalize(() => (this.inFlight = null)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    return this.inFlight;
  }

  /**
   * The CSRF header every refresh carries, plus the organization this tab wants to stay in when it
   * has one.
   *
   * A tab's organization lives in its in-memory token, which a reload destroys - and the refresh
   * that follows rebuilds claims from User.LastSelectedOrganizationId, a column shared by every
   * session. Without naming it here a reloaded tab silently adopts whichever organization was picked
   * last, in any tab. The server treats it as a request and validates it like any other selection,
   * so this cannot reach an organization the user could not otherwise select.
   */
  private refreshHeaders(): Record<string, string> {
    const headers: Record<string, string> = { [REFRESH_REQUEST_HEADER]: '1' };

    const organizationId = this.tabOrganization.get();
    if (organizationId) headers[ORGANIZATION_HEADER] = String(organizationId);

    return headers;
  }

  /**
   * Deletes the refresh cookie. Targets the refresh route rather than /api/v1/logout because the
   * cookie is path-scoped to this one, and a browser only applies a cross-origin Set-Cookie for a
   * request that carried credentials - which under ng serve /api/v1/logout does not.
   *
   * Errors are swallowed: logout must complete and land the user on the login page whatever the
   * server says. The server-side logout revokes the credential too, so a failure here is survivable.
   */
  public revoke(): Observable<boolean> {
    this.tokenService.clearToken();

    return this.httpClient
      .delete(`${this.rootUrl}/${REFRESH_ENDPOINT}`, {
        withCredentials: true,
        headers: { [REFRESH_REQUEST_HEADER]: '1' },
      })
      .pipe(
        map(() => true),
        catchError(() => of(false)),
      );
  }
}
